/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import React, { FC, useMemo, useEffect, useCallback } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Department } from "types/models/menu"
import { SortableItemList } from "components/common"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useTranslationsMap } from "common/utils/i18n"

import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
} from "queries"
import { mapHierarchyEntitiesToDepartments } from "utils/menuV3Mapper"
import LoadMore from "components/common/LoadMore"
import {
  getItemWithoutPhotoCountFromDepartment,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import commonStyles from "styles/common.modules.css"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"
import { MenuDepartmentRowV3 } from "./MenuDepartmentRowV3"
import { MenuDepartmentRowLoading } from "./MenuDepartmentRowLoading"
import { MenuPartialError } from "./MenuPartialError"
import { buildMenuActionItems, MenuActionKey } from "./menuActionItems"

export const MenuOverviewDepartmentsV3: FC = () => {
  const dispatch = useDispatch()

  const { isEditAllowed } = useMenuEditorStatus()
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    itemFilter,
    overview: { department: selectedDepartment },
  } = useSelector((state: AppState) => state?.menu)

  const {
    currentData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMenuV3HierarchiesInfinite(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !merchantID,
    },
  )

  // Flatten all pages and map to Department[]
  // Use currentData to ensure we only get data for current query args
  // RTK Query creates separate cache keys for different query parameters
  const departments = useMemo(() => {
    const allEntities = flattenInfiniteHierarchies(currentData)
    return mapHierarchyEntitiesToDepartments(allEntities || [])
  }, [currentData])

  // Filter departments based on itemFilter
  // TODO: check item without photo flow
  const filteredDepartments = useMemo(() => {
    if (isItemWithoutPhotoMode(itemFilter)) {
      return departments.filter(department => {
        return getItemWithoutPhotoCountFromDepartment(department) > 0
      })
    }
    return departments
  }, [departments, itemFilter])

  // Auto-select first department when departments are loaded
  // Clear selection when departments become empty
  useEffect(() => {
    if (!isLoading) {
      if (filteredDepartments.length > 0) {
        const previouslySelected = selectedDepartment
          ? filteredDepartments.find(
              dept => dept.itemClassID === selectedDepartment.itemClassID,
            )
          : null

        if (!previouslySelected) {
          dispatch(actionCreator.menu.selectDepartment(filteredDepartments[0]))
        }
      } else if (selectedDepartment) {
        // Clear selection when departments become empty to prevent stale queries
        dispatch(actionCreator.menu.selectDepartment(null))
      }
    }
  }, [isLoading, filteredDepartments, selectedDepartment, dispatch])

  // Load more handler
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const textMap = useTranslationsMap({
    rearrange: "menu.actions.rearrange",
    selectMultiple: "menu.actions.selectMultiple",
    categoriesTitle: "menu.headings.categories",
  })

  const isItemSelected = (department: Department) =>
    department.itemClassID === selectedDepartment?.itemClassID

  const handleMenuClick = useCallback(
    (key: MenuActionKey) => {
      switch (key) {
        case MenuActionKey.REARRANGE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showSortDepartments: true,
            }),
          )
          break
        case MenuActionKey.SELECT_MULTIPLE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showItemSelection: true,
            }),
          )
          break
        default:
          return
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.clickDepartmentMore,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          NUM_DEPARTMENT: departments?.length ?? 0,
          VALUE: key,
          IS_PARTIAL_MENU: true,
        },
      })
    },
    [dispatch, merchantID, merchantGroupID, menuGroupID, departments],
  )

  const handleSelectDepartment = (
    _: React.MouseEvent<HTMLElement>,
    department: Department,
  ) => {
    dispatch(actionCreator.menu.selectDepartment(department))

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickDepartmentName,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        DEPARTMENT_ID: department.itemClassID,
        IS_PARTIAL_MENU: true,
      },
    })
  }

  const canShowRearrange =
    isEditAllowed &&
    filteredDepartments.length > 0 &&
    selectedDepartment !== null

  const menuItems = buildMenuActionItems({
    canShowRearrange,
    rearrange: {
      key: MenuActionKey.REARRANGE,
      label: textMap.rearrange,
      onClick: () => handleMenuClick(MenuActionKey.REARRANGE),
    },
    selectMultiple: {
      key: MenuActionKey.SELECT_MULTIPLE,
      label: textMap.selectMultiple,
      onClick: () => handleMenuClick(MenuActionKey.SELECT_MULTIPLE),
    },
  })

  const renderRow = (department: Department) => {
    return <MenuDepartmentRowV3 department={department} />
  }

  const renderLoadMore = () => {
    if (hasNextPage && !error) {
      return (
        <LoadMore
          onLoadMore={handleLoadMore}
          component={<MenuDepartmentRowLoading count={3} />}
        />
      )
    }
    return null
  }

  // Show loading state when:
  // 1. Initial load (isLoading) - no data for current query args yet
  // 2. Fetching but no currentData - means we're fetching for new query args
  // 3. Error exists but we're fetching (user clicked retry) - show loading immediately
  // Note: RTK Query creates separate cache keys for different query parameters,
  // so currentData will be undefined for new cache keys until fetched
  if (isLoading || (isFetching && !currentData) || (error && isFetching)) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.categoriesTitle}</Col>
          </Row>
        }
      >
        <MenuDepartmentRowLoading />
      </Card>
    )
  }

  // Show error state
  if (error) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.categoriesTitle}</Col>
          </Row>
        }
      >
        <MenuPartialError error={error} onRefetch={refetch} />
      </Card>
    )
  }

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
      title={
        <Row justify="space-between">
          <Col>{textMap.categoriesTitle}</Col>
          {isEditAllowed && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{ items: menuItems }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredDepartments}
        renderRowContent={renderRow}
        renderLoadMore={renderLoadMore}
        isItemSelected={isItemSelected}
        onRowClick={handleSelectDepartment}
        useDragHandle
      />
    </Card>
  )
}
